const API_BASE = '/platform/api';

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * A request whose session generation ended before it finished (cover, expiry, sign-out, a new
 * operator, another tab), or that was attempted with no active session. Its answer — success or
 * error — belongs to nobody any more and is discarded.
 */
export class StaleAuthError extends Error {
  constructor() {
    super('The operator session changed; this answer was discarded');
    this.name = 'StaleAuthError';
  }
}

// #583 S5: the session owner (AuthContext) supplies the current auth snapshot. The client never
// refreshes, never reads or clears storage, and only REPORTS a 401 or an auth-lookup 503.
let getAuth = () => null;
let onUnauthorized = () => {};
let onAuthUnavailable = () => {};
let onMutationOutcome = () => {};

export function configureClient(binding) {
  getAuth = binding.getAuth;
  onUnauthorized = binding.onUnauthorized;
  onAuthUnavailable = binding.onAuthUnavailable;
  onMutationOutcome = binding.onMutationOutcome;
}

async function send(path, { method = 'GET', body, token, signal } = {}) {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    // An abort stays an abort. Anything else is a lost connection, said plainly.
    if (err?.name === 'AbortError') throw err;
    throw new Error('Could not reach the server', { cause: err });
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(data?.message || data?.error || res.statusText, res.status, data);
  return data;
}

const isCurrent = (auth, options) => {
  const current = getAuth();
  return current?.generation === auth.generation && current?.sessionId === auth.sessionId
    && options.isCurrentRequest?.() !== false;
};

/**
 * What a late answer says about a mutation. A 4xx is the server refusing it; a 5xx may come from a
 * proxy after the backend committed, and a lost response says nothing, so both are unknown.
 */
const outcomeOf = (err) => (err instanceof ApiError && err.status >= 400 && err.status < 500 ? 'refused' : 'unknown');

/** Discard a stale answer. A mutation's outcome is still reported, never silently dropped. */
function discard(auth, unsafe, outcome) {
  const stale = new StaleAuthError();
  if (unsafe) {
    stale.outcome = outcome;
    onMutationOutcome({ outcome, operatorId: auth.operatorId });
  }
  return stale;
}

/** A protected request, bound to the generation that was active when it started. */
async function request(path, options = {}) {
  const auth = getAuth();
  if (!auth) throw new StaleAuthError();
  // A mutation, once sent, is never aborted: the server may already have committed it (#583 G1).
  const unsafe = (options.method || 'GET') !== 'GET';
  let data;
  try {
    data = await send(path, { ...options, token: auth.token, signal: unsafe ? undefined : auth.signal });
  } catch (err) {
    if (!isCurrent(auth, options)) throw discard(auth, unsafe, outcomeOf(err));
    const report = { generation: auth.generation, sessionId: auth.sessionId, error: err.data };
    if (err.status === 401) onUnauthorized(report);
    if (err.status === 503 && err.data?.type === 'PLATFORM_AUTH_UNAVAILABLE') onAuthUnavailable(report);
    throw err;
  }
  if (!isCurrent(auth, options)) throw discard(auth, unsafe, 'succeeded');
  return data;
}

export const api = {
  login(email, password) {
    return send('/auth/login', { method: 'POST', body: { email, password } });
  },

  /** Validate a named credential. The caller (the session owner) handles its 401 itself. */
  getSession(token, signal) {
    return send('/auth/session', { token, signal });
  },

  /** Revoke the whole session behind this access credential. Bodyless by design (#583 D3). */
  logout(token, signal) {
    return send('/auth/logout', { method: 'POST', token, signal });
  },

  /** The four counters, from one server statement: { asOf, tenants, users } (#583 S4, §7.4). */
  getDashboard(options) {
    return request('/dashboard', options);
  },

  // ── Tenant Management ──────────────────────────────────────

  listTenants(params = {}, options) {
    const qs = new URLSearchParams(params).toString();
    return request(`/tenants${qs ? `?${qs}` : ''}`, options);
  },

  getTenant(id, options) {
    return request(`/tenants/${id}`, options);
  },

  lockTenant(id, reason, options) {
    return request(`/tenants/${id}/lock`, { ...options, method: 'POST', body: { reason } });
  },

  unlockTenant(id, options) {
    return request(`/tenants/${id}/unlock`, { ...options, method: 'POST' });
  },

  // ── User Management ────────────────────────────────────────

  searchUsers(params = {}, options) {
    const qs = new URLSearchParams(params).toString();
    return request(`/users/search${qs ? `?${qs}` : ''}`, options);
  },

  getUser(id, options) {
    return request(`/users/${id}`, options);
  },

  lockUser(id, reason, options) {
    return request(`/users/${id}/lock`, { ...options, method: 'POST', body: { reason } });
  },

  unlockUser(id, options) {
    return request(`/users/${id}/unlock`, { ...options, method: 'POST' });
  },

  resetUserPassword(id, password, options) {
    return request(`/users/${id}/reset-password`, { ...options, method: 'POST', body: { password } });
  },

  // ── Audit (#583 S4 read APIs) ──────────────────────────────

  /** One keyset page, newest first: { items, nextCursor, asOf }. `params` holds only S4 filters. */
  listAudit(params = {}, options) {
    const qs = new URLSearchParams(params).toString();
    return request(`/audit${qs ? `?${qs}` : ''}`, options);
  },

  getAuditEntry(id, options) {
    return request(`/audit/${encodeURIComponent(id)}`, options);
  },
};
